// Pomerový týždenný cieľ pre weekly_count návyky (Plán A).
// Prvý (neúplný) kalendárny týždeň po založení návyku sa nesmie vyhodnotiť ako
// zlyhaný — cieľ sa pomerovo zníži podľa počtu dní, ktoré návyk v tom týždni
// existoval; od nasledujúceho pondelka platí plný cieľ.
//
// MIRROR: db.py `week_target_for` zrkadlí presne túto logiku (rovnaké spec
// príklady v oboch testoch chytajú drift medzi JS a Pythonom).
//
// Spec príklady:
//   založenie pondelok           → plný cieľ
//   založenie štvrtok, cieľ 5    → 3   (zostávajú 4 dni: ceil(5×4/7) = 3)
//   založenie nedeľa             → 1

/** ISO 'YYYY-MM-DD' → počet dní od epochy (bez časových zón). */
function dayNum(iso: string): number {
  return Math.floor(Date.parse(iso + "T00:00:00Z") / 86400000);
}

/**
 * Týždenný cieľ weekly_count návyku pre kalendárny týždeň začínajúci
 * `weekMonday` (ISO 'YYYY-MM-DD', pondelok).
 *
 * - návyk založený pred/na pondelok → plný cieľ
 * - návyk založený až po nedeli → 0 (v tom týždni neexistoval)
 * - inak pomerovo: max(1, ceil(fullTarget × zostávajúce_dni / 7)),
 *   kde zostávajúce_dni = dni od založenia po nedeľu vrátane.
 */
export function weekTargetFor(created: string, weekMonday: string, fullTarget: number): number {
  const full = Math.max(1, fullTarget || 1);
  if (!created) return full;
  const c = dayNum(created);
  const mon = dayNum(weekMonday);
  if (!Number.isFinite(c) || !Number.isFinite(mon)) return full;
  if (c <= mon) return full;
  const sun = mon + 6;
  if (c > sun) return 0; // návyk v tom týždni ešte neexistoval
  const remaining = sun - c + 1;
  if (remaining <= 2) return 1; // založené v Sob/Ned → v prvom neúplnom týždni stačí 1×
  return Math.max(1, Math.ceil((full * remaining) / 7));
}

// ── Custom „N× za obdobie" frekvencia (recur_mode='period') ────────────────────
// Období: týždeň (Po–Ne), mesiac (kalendárny), pol rok (jan–jún / júl–dec), rok.
// MIRROR: db.py `period_start` / `period_end` / `period_target_for` zrkadlia túto
// logiku (rovnaké spec príklady v oboch testoch chytajú drift medzi JS a Pythonom).

export type RecurPeriod = "week" | "month" | "half" | "year";

/** Koľko výskytov za obdobie sa ešte považuje za „naozaj zriedkavé" → sekcia „Máš čas".
 *  mesiac ≤ 3 · pol rok ≤ 6 · rok ≤ 12. Týždeň sem nikdy nepatrí (bežná frekvencia). */
export const MASCAS_MAX: Record<string, number> = { month: 3, half: 6, year: 12 };

/** Patrí návyk „N× za <period>" do samostatnej sekcie „Máš čas" (zriedkavé)? */
export function isMasCas(period: string | null | undefined, count: number): boolean {
  if (!period) return false;
  const max = MASCAS_MAX[period];
  return max != null && (count || 1) <= max;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}
function isoFromDayNum(n: number): string {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

/** ISO začiatok obdobia, ktoré obsahuje `iso` (týždeň → pondelok). */
export function periodStart(period: RecurPeriod, iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  if (period === "month") return `${y}-${pad2(m)}-01`;
  if (period === "half") return `${y}-${m <= 6 ? "01" : "07"}-01`;
  if (period === "year") return `${y}-01-01`;
  const dn = dayNum(iso); // 1970-01-01 = Thu → pondelok = dn − ((dn+3) mod 7)
  return isoFromDayNum(dn - (((dn + 3) % 7) + 7) % 7);
}

/** ISO začiatok NASLEDUJÚCEHO obdobia po tom, ktoré začína `startIso`. */
export function nextPeriodStart(period: RecurPeriod, startIso: string): string {
  const [y, m] = startIso.split("-").map(Number);
  if (period === "month") return m === 12 ? `${y + 1}-01-01` : `${y}-${pad2(m + 1)}-01`;
  if (period === "half") return m <= 6 ? `${y}-07-01` : `${y + 1}-01-01`;
  if (period === "year") return `${y + 1}-01-01`;
  return isoFromDayNum(dayNum(startIso) + 7);
}

/** ISO posledný (vrátane) deň obdobia, ktoré začína `startIso`. */
export function periodEnd(period: RecurPeriod, startIso: string): string {
  return isoFromDayNum(dayNum(nextPeriodStart(period, startIso)) - 1);
}

/**
 * Pomerový cieľ „N× za obdobie" pre obdobie začínajúce `startIso`.
 * Rovnaká myšlienka ako weekTargetFor, zovšeobecnená na ľubovoľné obdobie:
 * - návyk založený pred/na začiatok obdobia → plný cieľ
 * - návyk založený až po konci obdobia → 0 (vtedy ešte neexistoval)
 * - inak pomerovo: max(1, ceil(full × zostávajúce_dni / dĺžka_obdobia)).
 */
export function periodTargetFor(created: string, startIso: string, period: RecurPeriod, fullTarget: number): number {
  const full = Math.max(1, fullTarget || 1);
  if (!created) return full;
  const c = dayNum(created);
  const start = dayNum(startIso);
  if (!Number.isFinite(c) || !Number.isFinite(start)) return full;
  if (c <= start) return full;
  const end = dayNum(periodEnd(period, startIso));
  if (c > end) return 0;
  const remaining = end - c + 1;
  const total = end - start + 1;
  if (period === "week" && remaining <= 2) return 1; // zrkadlí weekTargetFor: Sob/Ned → 1×
  return Math.max(1, Math.ceil((full * remaining) / total));
}

/**
 * F1 — počíta sa „N× za obdobie" návyk (weekly_count | period) do MENOVATEĽA
 * perfektného dňa `dateIso`? Nemení viditeľnosť — iba to, či daný deň JE povinné
 * mať návyk splnený, aby bol deň perfektný.
 *
 * Runway: remaining = fullTarget − doneThisPeriod; daysLeft = dni od dateIso po koniec
 * obdobia vrátane dateIso. Povinný iff remaining>0 AND daysLeft ≤ remaining — kým je
 * rezerva, perfektný deň je možný aj bez neho; keď rezerva zmizne, stáva sa povinným.
 * Grace pri založení: v období založenia nikdy povinný, ak návyk vznikol tak neskoro,
 * že sa opakovania nedali rozložiť — daysLeftAtCreation ≤ fullTarget+1.
 *
 * MIRROR: db.py `perfect_required_on` (rovnaké spec príklady chytajú drift JS↔Python).
 * Pozn.: backend posiela hotové `perfect_required` pole v state payloade; táto funkcia
 * je najmä zrkadlo pre testy (a prípadné FE-side prepočty).
 */
export function perfectRequiredOn(
  created: string,
  period: RecurPeriod,
  fullTarget: number,
  doneThisPeriod: number,
  dateIso: string,
): boolean {
  const full = Math.max(1, fullTarget || 1);
  const start = periodStart(period, dateIso);
  const endN = dayNum(periodEnd(period, start));
  const dN = dayNum(dateIso);
  if (created) {
    const cN = dayNum(created);
    if (Number.isFinite(cN)) {
      if (dN < cN) return false; // návyk vtedy ešte neexistoval
      if (periodStart(period, created) === start) {
        // obdobie založenia — grace, ak založený príliš neskoro na rozloženie
        const daysLeftAtCreation = endN - cN + 1;
        if (daysLeftAtCreation <= full + 1) return false;
      }
    }
  }
  const remaining = full - (doneThisPeriod || 0);
  if (remaining <= 0) return false;
  const daysLeft = endN - dN + 1;
  return daysLeft <= remaining;
}
